import React, { useEffect, useState } from "react";
import DashboardLayout from "../DashboardLayout";
import apiClient from "../../utils/apiClient";
import StocksScreenerTable from "./StocksScreenerTable";
import { SearchIcon } from "lucide-react";
import server from "../../utils/serverConfig";

export default function StocksScreener() {
  const [data, setData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const [isDataUpdated, setIsDataUpdated] = useState(false);

  const [combinedData, setCombinedData] = useState([]);

  const fetchAdditionUnderlyingStocks = async () => {
    let config = {
      method: "get",
      url: `${server.godseye.baseUrl}get-underlying-stocks-list`,
    };

    try {
      const response = await apiClient.request(config);
      return response.data.data; // Return the data
    } catch (error) {
      console.log(error);
      return []; // Return an empty array in case of error
    }
  };

  const fetchStocksScreener = async () => {
    let config = {
      method: "get",
      url: `${server.godseye.baseUrl}stocks_screener`,
    };

    try {
      const response = await apiClient.request(config);
      return response.data; // Return the data
    } catch (error) {
      console.log(error);
      return []; // Return an empty array in case of error
    }
  };

  const fetchSourceTags = async () => {
    try {
      const config = {
        method: "get",
        url: `${server.godseye.baseUrl}source-tags`,
      };
      const response = await apiClient.request(config);

      const today = new Date();
      const todayDateString = today.toISOString().split("T")[0];

      const enrichedData = response.data.map((tag) => ({
        ...tag,
        call: 0,
        put: 0,
      }));

      const filteredSortedData = enrichedData
        ?.filter((item) => item.date.startsWith(todayDateString))
        ?.sort((a, b) => new Date(b.date) - new Date(a.date));

      // setSourceTagsData(filteredSortedData);
      return filteredSortedData;
    } catch (error) {
      console.error(error);
      throw error; // Rethrow the error if needed for further handling
    }
  };

  const combineFinalDataAndSourceTags = (finalData, sourceTagsData) => {
    // Helper function to transform sources into objects with value and source type
    const transformSources = (sources, tagType) =>
      sources.map((source) => ({
        value: source,
        source: tagType,
      }));

    const combined = [...finalData, ...sourceTagsData].reduce((acc, curr) => {
      const existing = acc.find((item) => item.symbol === curr.symbol);

      if (existing) {
        // Transform sources from the current entry
        const newSources = transformSources(
          curr.sources || [],
          sourceTagsData.includes(curr) ? "source_tag" : "screener_tag"
        );

        // Combine and deduplicate sources
        const mergedSources = [
          ...existing.sources,
          ...newSources.filter(
            (newSource) =>
              !existing.sources.some((src) => src.value === newSource.value)
          ),
        ];

        existing.sources = mergedSources;
      } else {
        // Add a new entry with transformed sources
        acc.push({
          symbol: curr.symbol || curr["Underlying scrip code"],
          updated_at: curr.updated_at || curr.date,
          sources: transformSources(
            curr.sources || [],
            sourceTagsData.includes(curr) ? "source_tag" : "screener_tag"
          ),
          call: curr.call || 0,
          put: curr.put || 0,
        });
      }

      return acc;
    }, []);

    return combined;
  };

  useEffect(() => {
    const fetchDataAndCombine = async () => {
      const underlyingStocksData = await fetchAdditionUnderlyingStocks();
      const stocksScreenerData = await fetchStocksScreener();
      const sourceTagsData = await fetchSourceTags();

      let preprocessedData = [];
      let preprocessedSourceTags = [];

      // Preprocess underlying stocks data
      if (underlyingStocksData && underlyingStocksData.length > 0) {
        preprocessedData = stocksScreenerData.map((ele) => {
          const stock = underlyingStocksData.find(
            (stock) => stock.symbol === ele["Underlying scrip code"]
          );
          const call = stock ? parseInt(stock.call, 10) : 0;
          const put = stock ? parseInt(stock.put, 10) : 0;
          return { ...ele, symbol: ele["Underlying scrip code"], call, put };
        });
      }

      // Preprocess source tags data
      if (sourceTagsData && sourceTagsData.length > 0) {
        preprocessedSourceTags = sourceTagsData.map((tag) => {
          // Find matching stock data from underlyingStocksData
          const stock = underlyingStocksData.find(
            (stock) => stock.symbol === tag.symbol
          );

          // Extract or default to 0 if not found, similar to underlyingStocksData preprocessing
          const call = stock ? parseInt(stock.call, 10) : 0;
          const put = stock ? parseInt(stock.put, 10) : 0;

        
          return { ...tag,symbol: tag.symbol,call,put};
        });
      }

      console.log("Preprocessed source tags data:", preprocessedSourceTags);

      // Combine the data using the new function
      const combined = combineFinalDataAndSourceTags(
        preprocessedData,
        preprocessedSourceTags
      );
      setCombinedData(combined);
    };

    fetchDataAndCombine();
  }, [isDataUpdated]);

  // Filter data based on search query
  const filteredData =
    combinedData &&
    combinedData.filter((item) =>
      item.sources.some((source) =>
        source?.value
          .replace(/\s+/g, "")
          .toLowerCase()
          .includes(searchQuery.replace(/\s+/g, "").toLowerCase())
      )
    );

  console.log("Combined data is ", combinedData);

  return (
    <DashboardLayout>
      <div className="flex flex-col relative w-full max-h-screen">
        <div className="flex items-center justify-between space-x-4 ">
          <div className="flex items-center justify-between bg-[#0F0F0F] h-[60px] px-6 w-full">
            <p className="flex items-center text-lg font-semibold text-white">
              Stocks Screener Table
            </p>
            <div className="flex items-center space-x-1 bg-zinc-900  px-4">
              <SearchIcon className="h-5 w-5 text-white" />
              <input
                type="text"
                className="bg-zinc-900 text-white rounded px-4 py-3 text-sm focus:outline-none"
                placeholder="Search by Source"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>

        <div className="w-full h-full overflow-hidden">
          <StocksScreenerTable
            data={filteredData}
            isDataUpdated={isDataUpdated}
            setIsDataUpdated={setIsDataUpdated}
          />
        </div>
      </div>
    </DashboardLayout>
  );
}
